import * as React from 'react'

/**
 * Animated container for modal content whose height changes on interaction
 * (#944). When `switchKey` changes, the outgoing content is snapshotted as an
 * inert DOM clone and crossfaded under the incoming pane while the container
 * height animates between the two measured values — height is not
 * auto-animatable, so it's measured px→px and released back to auto at the
 * end. Content growth WITHIN a pane ("Show all N amenities", async sections
 * filling in) is tweened too, via ResizeObserver.
 *
 * Motion is driven entirely by inline styles from JS — no stylesheet
 * animations — so the explorer.css reduced-motion kill-list contract is
 * untouched; reduced motion is honored with a live matchMedia check (the
 * widget-core preloader precedent) and degrades to an instant swap.
 *
 * Layout: the wrapper and inner div are nested min-height:0 flex columns, so
 * a flex-item child like `.hfse-sbp-what-pane` (flex: 1 1 auto; overflow-y:
 * auto) keeps its scroll contract inside a max-height'd flex-column dialog.
 * In a plain block context (the mobile sheet's inline stages) the flex styles
 * are inert and the wrapper behaves like a div.
 *
 * The morph only arms when the wrapper has a real height (offsetHeight > 0):
 * a zero-height wrapper has nothing visible to animate, and headless test
 * DOMs measure 0 — unit tests get the instant swap unless they mock
 * measurements.
 */

const HEIGHT_MS = 220
const FADE_MS = 180
const FADE_IN_DELAY_MS = 60

function prefersReducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
}

const wrapStyle: React.CSSProperties = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minHeight: 0,
}
const innerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minHeight: 0,
}

interface PendingMorph {
  fromHeight: number
  ghost: HTMLElement
}

export function AnimatedHeightSwitch({
  switchKey,
  children,
  className,
}: {
  switchKey: string
  children: React.ReactNode
  className?: string
}) {
  const wrapRef = React.useRef<HTMLDivElement>(null)
  const innerRef = React.useRef<HTMLDivElement>(null)
  const pendingRef = React.useRef<PendingMorph | null>(null)
  const animatingRef = React.useRef(false)
  const lastHeightRef = React.useRef<number | null>(null)
  const cleanupRef = React.useRef<(() => void) | null>(null)

  // The incoming children arrive one render BEFORE this component adopts the
  // new key (the parent evaluates its `tab === …` branches first), so the
  // previous render's children ARE the outgoing pane. Holding them in a ref
  // keeps the outgoing DOM mounted for exactly one commit — the capture
  // window — without any render-phase setState churn.
  const [displayKey, setDisplayKey] = React.useState(switchKey)
  const lastChildrenRef = React.useRef(children)
  if (switchKey === displayKey) lastChildrenRef.current = children
  const shown = switchKey === displayKey ? children : lastChildrenRef.current

  const startHeightTween = React.useCallback((wrap: HTMLDivElement, from: number, to: number, release: () => void) => {
    animatingRef.current = true
    wrap.style.height = `${from}px`
    wrap.style.overflow = 'hidden'
    // Style flush so the transition sees the starting height.
    void wrap.offsetHeight
    wrap.style.transition = `height ${HEIGHT_MS}ms ease`
    wrap.style.height = `${to}px`
    const timer = setTimeout(release, Math.max(HEIGHT_MS, FADE_MS + FADE_IN_DELAY_MS) + 50)
    cleanupRef.current = () => {
      clearTimeout(timer)
      release()
    }
  }, [])

  // Phase 1 — the key changed but the DOM still shows the outgoing pane:
  // capture its height and an inert clone, then adopt the new key.
  React.useLayoutEffect(() => {
    if (switchKey === displayKey) return
    const wrap = wrapRef.current
    const inner = innerRef.current
    if (wrap && inner && wrap.offsetHeight > 0 && !prefersReducedMotion()) {
      pendingRef.current = { fromHeight: wrap.offsetHeight, ghost: inner.cloneNode(true) as HTMLElement }
      // cloneNode drops scroll positions; the trees are structurally
      // identical, so copy them across by traversal index.
      const src = inner.querySelectorAll<HTMLElement>('*')
      const dst = pendingRef.current.ghost.querySelectorAll<HTMLElement>('*')
      for (let i = 0; i < src.length; i++) if (src[i].scrollTop) dst[i].scrollTop = src[i].scrollTop
    }
    setDisplayKey(switchKey)
  }, [switchKey, displayKey])

  // Phase 2 — the incoming pane just committed: overlay the ghost, crossfade,
  // and tween the container between the two measured heights.
  React.useLayoutEffect(() => {
    const pending = pendingRef.current
    if (!pending) return
    pendingRef.current = null
    const wrap = wrapRef.current
    const inner = innerRef.current
    if (!wrap || !inner) return
    cleanupRef.current?.()

    const { fromHeight, ghost } = pending
    const toHeight = wrap.offsetHeight

    ghost.setAttribute('aria-hidden', 'true')
    ghost.setAttribute('data-hr-height-ghost', '1')
    // Cloned buttons/inputs must not stay tab-reachable during the crossfade
    // — they carry no handlers. `inert` strips focus + AT in one attribute;
    // aria-hidden and pointer-events above remain the fallback where it is
    // unsupported.
    ghost.setAttribute('inert', '')
    Object.assign(ghost.style, {
      position: 'absolute',
      inset: '0',
      overflow: 'hidden',
      margin: '0',
      pointerEvents: 'none',
      opacity: '1',
      transition: `opacity ${FADE_MS}ms ease`,
    })
    wrap.appendChild(ghost)

    inner.style.opacity = '0'
    inner.style.transition = `opacity ${FADE_MS}ms ease ${FADE_IN_DELAY_MS}ms`

    const release = () => {
      cleanupRef.current = null
      animatingRef.current = false
      ghost.remove()
      wrap.style.removeProperty('height')
      wrap.style.removeProperty('overflow')
      wrap.style.removeProperty('transition')
      inner.style.removeProperty('opacity')
      inner.style.removeProperty('transition')
      lastHeightRef.current = wrap.offsetHeight
    }
    startHeightTween(wrap, fromHeight, toHeight, release)
    // Kick the fades in the same frame as the height tween.
    ghost.style.opacity = '0'
    inner.style.opacity = '1'
  }, [displayKey, startHeightTween])

  // Same-pane growth: tween any content resize that isn't part of an active
  // morph. Observing the INNER div while animating the wrapper cannot loop —
  // the inner's natural size is set by its content, not the wrapper's height.
  React.useEffect(() => {
    const wrap = wrapRef.current
    const inner = innerRef.current
    if (!wrap || !inner || typeof ResizeObserver === 'undefined') return
    lastHeightRef.current = wrap.offsetHeight
    const ro = new ResizeObserver(() => {
      if (animatingRef.current) return
      const from = lastHeightRef.current
      const to = wrap.offsetHeight
      lastHeightRef.current = to
      if (from == null || from === 0 || Math.abs(to - from) <= 1 || prefersReducedMotion()) return
      cleanupRef.current?.()
      startHeightTween(wrap, from, to, () => {
        cleanupRef.current = null
        animatingRef.current = false
        wrap.style.removeProperty('height')
        wrap.style.removeProperty('overflow')
        wrap.style.removeProperty('transition')
        lastHeightRef.current = wrap.offsetHeight
      })
    })
    ro.observe(inner)
    return () => ro.disconnect()
  }, [startHeightTween])

  // Interrupted-unmount hygiene: a live morph's ghost and pinned height die
  // with the component.
  React.useEffect(() => () => cleanupRef.current?.(), [])

  return (
    <div ref={wrapRef} className={className} style={wrapStyle}>
      <div ref={innerRef} style={innerStyle}>
        {shown}
      </div>
    </div>
  )
}
